import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../lib/apiClient";

// SCRUM-103 — centralized request system. `form_template` is the first type.
export type RequestType = "form_template";
export type RequestStatus = "pending" | "approved" | "rejected" | "fulfilled";

export interface PlatformRequest {
  id: string;
  group_home_id: string;
  requested_by: string;
  request_type: RequestType;
  subject: string;
  details: string | null;
  metadata: Record<string, unknown> | null;
  status: RequestStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreatePlatformRequestPayload {
  request_type: RequestType;
  subject: string;
  details?: string | null;
  metadata?: Record<string, unknown> | null;
}

/** List requests. Platform reviewers see all; group-home users see their own. */
export function usePlatformRequests(
  status?: string,
  options?: { enabled?: boolean },
) {
  return useQuery<PlatformRequest[]>({
    queryKey: ["platform-requests", status ?? "all"],
    queryFn: async () => {
      const query = status ? `?status=${encodeURIComponent(status)}` : "";
      const { data } = await api.get(`/platform-requests${query}`);
      return data.data;
    },
    enabled: options?.enabled ?? true,
  });
}

/** Submit a new request (group-home managers). */
export function useCreatePlatformRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    // US-081: component inspects response.data.details for per-field errors, so
    // it owns the error toast — suppress the global handler.
    meta: { suppressErrorNotification: true },
    mutationFn: async (payload: CreatePlatformRequestPayload) => {
      const { data } = await api.post("/platform-requests", payload);
      return data.data as PlatformRequest;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["platform-requests"] }),
  });
}

/** Process a request (platform reviewers): approve / reject / fulfil. */
export function useReviewPlatformRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    // US-081: error fallback centralized here; RequestsPage catch is sole caller.
    meta: { errorFallback: "Failed to update request" },
    mutationFn: async (input: {
      id: string;
      status: Exclude<RequestStatus, "pending">;
      review_notes?: string | null;
    }) => {
      const { id, ...body } = input;
      const { data } = await api.patch(`/platform-requests/${id}`, body);
      return data.data as PlatformRequest;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["platform-requests"] }),
  });
}
